import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, CheckCircle, Clock, X, User, Mail, AlertCircle } from 'lucide-react';

// ===== CUSTOMER SUPPORT COMPONENT (For Users) =====
export const CustomerSupport = ({ currentUser, onBack }) => {
  const [complaints, setComplaints] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    subject: '',
    description: ''
  });

  // Load complaints from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('supportComplaints');
    if (stored) {
      setComplaints(JSON.parse(stored));
    }
  }, []);

  // Save complaints to localStorage whenever they change
  useEffect(() => {
    if (complaints.length > 0) {
      localStorage.setItem('supportComplaints', JSON.stringify(complaints));
    }
  }, [complaints]);

  // Get user's complaints only
  const userComplaints = complaints.filter(c => c.userId === currentUser?.id);

  const handleSubmit = () => {
    if (!formData.subject || !formData.description) {
      alert('Please fill in all fields');
      return;
    }

    const newComplaint = {
      id: Date.now(),
      userId: currentUser.id,
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      description: formData.description,
      status: 'pending',
      createdAt: new Date().toISOString(),
      response: null,
      respondedAt: null
    };

    const updatedComplaints = [...complaints, newComplaint];
    setComplaints(updatedComplaints);
    localStorage.setItem('supportComplaints', JSON.stringify(updatedComplaints));

    setFormData({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      subject: '',
      description: ''
    });
    setShowForm(false);
    alert('Your complaint has been submitted successfully!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button onClick={onBack} className="text-indigo-600 hover:text-indigo-700 mb-2">
            ← Back
          </button>
          <h2 className="text-3xl font-bold text-gray-800">Customer Support</h2>
          <p className="text-gray-600 mt-1">Submit and track your complaints</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <MessageSquare className="w-4 h-4" />
          New Complaint
        </button>
      </div>

      {/* Complaint Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-indigo-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">Submit a Complaint</h3>
            <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Brief description of your issue"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Please provide detailed information about your complaint..."
                rows={5}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <button
              onClick={handleSubmit}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2 font-medium"
            >
              <Send className="w-4 h-4" />
              Submit Complaint
            </button>
          </div>
        </div>
      )}

      {/* User's Complaints List */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-800">Your Complaints ({userComplaints.length})</h3>
        
        {userComplaints.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No complaints submitted yet</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
            >
              Submit Your First Complaint
            </button>
          </div>
        ) : (
          userComplaints.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(complaint => (
            <div key={complaint.id} className="bg-white rounded-xl shadow-md p-6 border-l-4 border-indigo-500">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-800">{complaint.subject}</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Submitted on {new Date(complaint.createdAt).toLocaleDateString()} at{' '}
                    {new Date(complaint.createdAt).toLocaleTimeString()}
                  </p>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(complaint.status)}`}>
                  {getStatusIcon(complaint.status)}
                  {complaint.status.toUpperCase()}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Your Complaint:</p>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{complaint.description}</p>
              </div>

              {complaint.response && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-800 mb-2">Admin Response:</p>
                  <p className="text-gray-700">{complaint.response}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Responded on {new Date(complaint.respondedAt).toLocaleDateString()} at{' '}
                    {new Date(complaint.respondedAt).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ===== ADMIN SUPPORT COMPONENT (For Admin) =====
export const AdminSupport = ({ onBack }) => {
  const [complaints, setComplaints] = useState([]);
  const [filter, setFilter] = useState('all');
  const [responseText, setResponseText] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem('supportComplaints');
    if (stored) {
      setComplaints(JSON.parse(stored));
    }
  }, []);

  const handleRespond = (complaintId) => {
    const response = responseText[complaintId];
    if (!response || !response.trim()) {
      alert('Please enter a response');
      return;
    }

    const updatedComplaints = complaints.map(c => 
      c.id === complaintId 
        ? { 
            ...c, 
            status: 'resolved', 
            response: response,
            respondedAt: new Date().toISOString()
          }
        : c
    );

    setComplaints(updatedComplaints);
    localStorage.setItem('supportComplaints', JSON.stringify(updatedComplaints));
    setResponseText(prev => ({ ...prev, [complaintId]: '' }));
    alert('Response sent successfully!');
  };

  const filteredComplaints = complaints.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const pendingCount = complaints.filter(c => c.status === 'pending').length;
  const resolvedCount = complaints.filter(c => c.status === 'resolved').length;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <button onClick={onBack} className="text-indigo-600 hover:text-indigo-700 mb-2">
          ← Back to Admin Panel
        </button>
        <h2 className="text-3xl font-bold text-gray-800">Customer Support Management</h2>
        <p className="text-gray-600 mt-1">View and respond to customer complaints</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Complaints</p>
              <p className="text-3xl font-bold text-gray-800">{complaints.length}</p>
            </div>
            <MessageSquare className="w-12 h-12 text-indigo-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
            <Clock className="w-12 h-12 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Resolved</p>
              <p className="text-3xl font-bold text-green-600">{resolvedCount}</p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex gap-3">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({complaints.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('resolved')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'resolved'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Resolved ({resolvedCount})
          </button>
        </div>
      </div>

      {/* Complaints List */}
      <div className="space-y-4">
        {filteredComplaints.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">
              {filter === 'all' ? 'No complaints yet' : `No ${filter} complaints`}
            </p>
          </div>
        ) : (
          filteredComplaints.map(complaint => (
            <div
              key={complaint.id}
              className={`bg-white rounded-xl shadow-md p-6 border-l-4 ${
                complaint.status === 'pending' ? 'border-yellow-500' : 'border-green-500'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-800">{complaint.subject}</h4>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {complaint.name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {complaint.email}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Submitted on {new Date(complaint.createdAt).toLocaleDateString()} at{' '}
                    {new Date(complaint.createdAt).toLocaleTimeString()}
                  </p>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                  complaint.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {complaint.status === 'pending' ? <Clock className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                  {complaint.status.toUpperCase()}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Complaint Details:</p>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{complaint.description}</p>
              </div>

              {complaint.status === 'pending' ? (
                <div className="space-y-3">
                  <textarea
                    value={responseText[complaint.id] || ''}
                    onChange={(e) => setResponseText(prev => ({ ...prev, [complaint.id]: e.target.value }))}
                    placeholder="Type your response here..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={() => handleRespond(complaint.id)}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 font-medium"
                  >
                    <Send className="w-4 h-4" />
                    Send Response & Mark as Resolved
                  </button>
                </div>
              ) : (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-800 mb-2">Your Response:</p>
                  <p className="text-gray-700">{complaint.response}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Responded on {new Date(complaint.respondedAt).toLocaleDateString()} at{' '}
                    {new Date(complaint.respondedAt).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};